/**
 * Check Batch Status Handler - 批量查询收藏状态处理器
 *
 * 负责处理 checkBatchFavoritesStatus API 的完整逻辑
 */

import log from "logbeacon";
import { CacheManager } from "../managers/CacheManager";

import type { FavoritesCenterState } from "../types";
import { OptimisticUpdateManager } from "../managers/OptimisticUpdateManager";
import type { PositionsAccountService } from "../PositionsAccountService";
import { UserStateManager } from "../managers/UserStateManager";
import { batchUpdateVersionedData } from "../managers/VersionedDataManager";
import type {
  PositionAsset,
  PositionAssetTypeEnum,
} from "@/src/features/api/memberApi";

export class CheckBatchStatusHandler {
  constructor(
    private userStateManager: UserStateManager,
    private cacheManager: CacheManager,
    private optimisticUpdateManager: OptimisticUpdateManager,
    private state: FavoritesCenterState,
    private positionsAccountService: PositionsAccountService | null,
    private filterNormalStatusPortfolios: (portfolioIds: string[]) => string[],
    private handleUserSwitch: () => Promise<void>,
    private getCurrentUserId: () => string | null,
    private handleError: (error: Error) => void,
  ) {}

  /**
   * Execute batch check favorites status
   */
  async execute(
    contentIds: string[],
    assetType: "TOKEN" | "STOCK" = "TOKEN",
    accountId?: string,
  ): Promise<void> {
    // 1. Check user switch
    await this.handleUserSwitch();

    // 2. Filter out uncached content IDs
    const uncachedIds = this.cacheManager.getUncachedContentIds(contentIds);

    if (uncachedIds.length === 0) {
      return; // All content is cached, no need to query
    }

    try {
      // v2.0: If accountId is not provided, try to get first account from userFolders
      let targetAccountId = accountId;
      if (
        !targetAccountId &&
        this.state.userFolders &&
        this.state.userFolders.length > 0
      ) {
        // Use first folder's ID as default accountId
        targetAccountId = this.state?.userFolders?.[0]?.id;
        log.debug(
          `[CheckBatchStatusHandler] Using first folder as default accountId: ${targetAccountId}`,
        );
      }

      // If no accountId, cannot query, mark all assets as unfavorited
      if (!targetAccountId) {
        log.warn(
          "[CheckBatchStatusHandler] No accountId provided and no userFolders available, marking all as unfavorited",
        );
        this.optimisticUpdateManager.markAsUnfavorited(uncachedIds);
        return;
      }

      // v2.0: Call getUserPositions to batch query favorites status
      const response = await this.positionsAccountService?.getUserPositions(
        targetAccountId,
        {
          asset_ids: uncachedIds,
          asset_type: assetType as PositionAssetTypeEnum,
        },
      );

      if (response?.success && response?.data) {
        // 4. Update source data and active data
        const currentUserId = this.getCurrentUserId();
        if (!currentUserId) {
          return;
        }

        const userData =
          this.userStateManager.getOrCreateUserData(currentUserId);

        // v2.0: Convert PositionAsset[] to { [contentId: string]: accountIds[] } format
        const positionAssets = response.data.rows || [];

        // Initialize all queried assets as empty array (handle assets not found)
        const favoriteStatesMap: Record<string, string[]> = {};
        uncachedIds.forEach((assetId) => {
          favoriteStatesMap[assetId] = [];
        });

        // Extract favorites status from PositionAsset[]
        positionAssets.forEach((item: PositionAsset) => {
          const contentId = item.asset_id;
          if (!contentId) {
            return;
          }

          // v2.0: Use account_id as folder ID (keep array format)
          // Prefer item.account_id, if not exists use targetAccountId
          const accountIdValue = item.account_id || targetAccountId;
          if (accountIdValue) {
            favoriteStatesMap[contentId] = [accountIdValue];
          }
        });

        // Batch update versioned data
        const updates = Object.entries(favoriteStatesMap).map(
          ([contentId, accountIds]) => {
            // Filter to keep only status = "NORMAL" folders
            const filteredAccountIds =
              this.filterNormalStatusPortfolios(accountIds);
            return { key: contentId, value: filteredAccountIds };
          },
        );

        // Update source data
        updates.forEach(({ key, value }) => {
          userData.favoriteStates.set(key, value);
        });

        // Batch update active data
        batchUpdateVersionedData(this.state.favoriteStates, updates, true);

        // Update allFavoriteData summary data
        this.optimisticUpdateManager.updateAllFavoriteData(updates);

        userData.lastAccess = Date.now();

        log.info(
          `[CheckBatchStatusHandler] Batch query favorites status completed: ${uncachedIds.length} items`,
        );
        log.debug("[CheckBatchStatusHandler] Batch query results:", {
          responseData: response.data,
          stateSize: Object.keys(this.state.favoriteStates.data).length,
          stateKeys: Object.keys(this.state.favoriteStates.data),
        });
      } else {
        log.error(
          "[CheckBatchStatusHandler] Failed to batch query favorites status:",
          response?.message,
        );
        // Failure fallback: mark uncached assets as unfavorited
        this.optimisticUpdateManager.markAsUnfavorited(uncachedIds);
      }
    } catch (error) {
      log.error(
        "[CheckBatchStatusHandler] Failed to batch query favorites status:",
        error,
      );
      this.handleError(error as Error);
      // Failure fallback: mark uncached assets as unfavorited
      this.optimisticUpdateManager.markAsUnfavorited(uncachedIds);
    }
  }
}
